import { useEffect, useState } from "react";
import { batteryLabel, splitBatteryChoices } from "./batteryOptions";

export default function BatterySizePicker({
  choices,
  value,
  onChange,
  tagFor,
  compact = false,
}: {
  choices: number[];
  value: number;
  onChange: (kwh: number) => void;
  tagFor?: (kwh: number) => string;
  /** Two columns, for a side panel that is already half the card. */
  compact?: boolean;
}) {
  const { primary, extra } = splitBatteryChoices(choices);
  const selectedIsExtra = extra.some((kwh) => Math.abs(kwh - value) < 0.06);
  const [open, setOpen] = useState(selectedIsExtra);

  useEffect(() => {
    if (selectedIsExtra) setOpen(true);
  }, [selectedIsExtra]);

  if (primary.length === 0 && extra.length === 0) return null;

  return (
    <div>
      <span className="mb-2 block font-bold">Home battery</span>
      {primary.length > 0 && (
        <div className={`grid grid-cols-2 gap-2 ${compact ? "" : "sm:grid-cols-4"}`}>
          {primary.map((kwh) => (
            <BatteryButton key={kwh} kwh={kwh} selected={Math.abs(value - kwh) < 0.06} tag={tagFor?.(kwh) ?? ""} onClick={() => onChange(kwh)} />
          ))}
        </div>
      )}
      {extra.length > 0 && (
        <>
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((current) => !current)}
            className="mt-3 text-left text-sm font-semibold text-indigo underline decoration-indigo/40 underline-offset-2 hover:decoration-indigo"
          >
            Need more storage?
          </button>
          {open && (
            <div className={`mt-2 grid grid-cols-2 gap-2 ${compact ? "" : "sm:grid-cols-4"}`}>
              {extra.map((kwh) => (
                <BatteryButton key={kwh} kwh={kwh} selected={Math.abs(value - kwh) < 0.06} tag={tagFor?.(kwh) ?? ""} onClick={() => onChange(kwh)} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function BatteryButton({
  kwh,
  selected,
  tag,
  onClick,
}: {
  kwh: number;
  selected: boolean;
  tag: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex min-h-14 flex-col items-center justify-center rounded-xl border-2 px-2 text-center text-[13px] font-bold leading-tight sm:text-sm ${
        selected ? "border-indigo bg-lavender text-indigoDark" : "border-border bg-white text-ink"
      }`}
    >
      {batteryLabel(kwh)}
      {tag ? <span className="text-[10px] tracking-widest text-mutedLight">{tag}</span> : null}
    </button>
  );
}
